import { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { MediaAsset } from "@/types/media";
import { ExternalLink, Loader2, X } from "lucide-react";

export function MediaPreviewDialog({
    item,
    open,
    onOpenChange,
}: {
    item: MediaAsset | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    const [loading, setLoading] = useState(true);

    // Reset loading state whenever the item changes
    useEffect(() => {
        if (open) setLoading(true);
    }, [item, open]);

    if (!item) return null;
    const url = (item as any).fileUrl || (item as any).s3Url || "";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl w-[95vw] p-0 border-none bg-black/95 sm:rounded-lg overflow-hidden gap-0">
                {/* Header - Transparent overlay on mobile, solid on desktop */}
                <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between p-4 bg-gradient-to-b from-black/60 to-transparent">
                    <h3 className="text-white text-sm font-medium truncate pr-4">
                        {item.fileName}
                    </h3>
                    <button
                        onClick={() => onOpenChange(false)}
                        className="text-white/80 hover:text-white transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="relative flex flex-col items-center justify-center min-h-[50vh] max-h-[90vh]">
                    {/* Spinner: Shown only while image is loading */}
                    {loading && (
                        <div className="absolute inset-0 flex items-center justify-center z-10">
                            <Loader2 className="h-8 w-8 text-white animate-spin opacity-50" />
                        </div>
                    )}

                    <img
                        src={url}
                        alt={item.fileName}
                        onLoad={() => setLoading(false)}
                        className={`
              max-w-full max-h-[85vh] object-contain transition-opacity duration-300
              ${loading ? "opacity-0" : "opacity-100"}
            `}
                    />

                    {/* Quick Actions Footer */}
                    {!loading && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3 px-4 py-2 bg-black/40 backdrop-blur-md rounded-full border border-white/10">
                            <a
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-white text-xs flex items-center gap-1.5 hover:text-blue-400 transition-colors"
                            >
                                <ExternalLink className="h-3.5 w-3.5" />
                                Original
                            </a>
                            <span className="text-white/30">|</span>
                            <span className="text-white/80 text-xs">
                                {(item as any).fileSize || "IMG"}
                            </span>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}